import { baseInputValidationScheme } from '../constants';
import { TContext, TTag } from './types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import UniversalChildren from '../../UniversalTag/UniversalChildren';
import { TInputState } from '../CustomForm/types';
import { TBaseTagContext } from '../types';
import { validate } from '@krugom/validator';

const CustomInputBoolean: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const baseScheme = baseInputValidationScheme;
  const validationScheme = useMemo(
    () => ({
      ...baseScheme,
      properties: {
        ...baseScheme.properties,
        initialValue: {
          type: 'boolean',
        },
      },
    }),
    [baseScheme]
  );

  const result = useMemo(() => validate(tagProps, validationScheme), [tagProps, validationScheme]);

  const {
    name,
    children,
    required,
    validation,
    initialValue,
    formOnly,
    label,
    description,
    placeholder,
  } = tagProps;

  const formContextInput = name ? context.form?.inputs[name] : undefined;
  const [showHelp, setShowHelp] = useState<boolean>(false);

  const initialState: TInputState = useMemo(
    () => ({
      name: name || 'unknown-boolean-input',
      label,
      description,
      placeholder,
      type: 'boolean',
      value: !!initialValue,
      errors: [],
      touched: false,
      required,
      validation,
      initialValue,
      formOnly,
      reset: false,
    }),
    [formOnly, initialValue, name, required, validation]
  );

  useEffect(() => {
    if (context.form) {
      if (!formContextInput) {
        context.form.handleSetInput(name, initialState);
      } else {
        if (formContextInput.reset) {
          context.form.handleSetInput(name, initialState);
        }
      }
    }
  }, [context.form, formContextInput, initialState, name]);

  const allowedTypes = useMemo(() => ['checkbox', 'radio'], []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!context.form) return;
      const inputType = e.target.type;
      const inputValue = e.target.checked;
      if (!allowedTypes.includes(inputType)) {
        console.warn(`Type ${inputType} is not supported!`);
        return;
      }
      context.form?.handleSetValue(name, inputValue);
    },
    [allowedTypes, context.form, name]
  );

  const handleShowHelp = useCallback(() => setShowHelp(showHelp), [showHelp]);

  const inputContext: TContext = useMemo(
    () => ({
      name,
      type: initialState.type,
      required,
      validation,
      initialValue,
      errors: formContextInput?.errors,
      touched: formContextInput?.touched,
      handleChange,
      handleShowHelp,
      value: !!formContextInput?.value,
      label,
      description,
      showHelp,
    }),
    [
      formContextInput?.errors,
      formContextInput?.touched,
      handleChange,
      handleShowHelp,
      initialState.type,
      initialValue,
      name,
      required,
      validation,
      showHelp,
      formContextInput?.value,
      label,
      description,
    ]
  );

  const newContexts: Partial<TBaseTagContext> = useMemo(
    () => ({
      ...context,
      input: inputContext,
    }),
    [context, inputContext]
  );

  if (!context.form) {
    console.warn('Tag <InputSimpleContext> must be child of the <FormContext> tag!');
    return <UniversalChildren context={context}>{tagProps.children}</UniversalChildren>;
  }

  if (!result.isValid) {
    console.error(
      'Тэг: $InputBooleanContext;',
      `Путь: ${context.path?.join('.') || 'Не указан'};`,
      result.errors
    );
    return null;
  }

  return <UniversalChildren context={newContexts}>{children}</UniversalChildren>;
});
CustomInputBoolean.displayName = 'CustomInputBoolean';
export default CustomInputBoolean;
